// Shared brand-blue accent override for placed objects.
//
// Both Scene and Iso views call this when an object's `accent: true` flag is
// set. The look must match between tabs: same colour, same emissive bias,
// same roughness/metalness, same applyOneQodeShader preset. Anything else
// (e.g. theme palette overrides) layers on top by passing a custom hex.
//
// The default `colorHex` matches the brand blue used by `mat-accent` in
// assetRegistry.matFor (`0x0b72b7`), so an accented object reads visually
// continuous with the always-blue accent surfaces baked into masters.

import * as THREE from "three";
import { applyOneQodeShader } from "./assetRegistry";
import type { BrandMaterialMode } from "./types";

export const DEFAULT_ACCENT_HEX = 0x0b72b7;
export const DEFAULT_ACCENT_CSS = "#0b72b7";
const LEGACY_ACCENT_CSS = "#1a4dd8";
const DEFAULT_EMISSIVE_HEX = 0x064b83;
const DEFAULT_SURFACE_HEX = 0x161719;
const DEFAULT_BRAND_WHITE_HEX = 0xf3f6fb;

export function normalizeAccentCss(value: string | undefined): string {
  const v = (value ?? "").trim();
  if (!v || v.toLowerCase() === LEGACY_ACCENT_CSS) return DEFAULT_ACCENT_CSS;
  return v;
}

export function createSurfaceMaterial(): THREE.MeshPhysicalMaterial {
  return applyOneQodeShader(new THREE.MeshPhysicalMaterial({
    color: DEFAULT_SURFACE_HEX,
    roughness: 0.85,
    metalness: 0,
    envMapIntensity: 0.10,
    clearcoat: 0.25,
    clearcoatRoughness: 0.70,
  }), "satin");
}

export function createAccentMaterial(
  colorHex: number = DEFAULT_ACCENT_HEX,
): THREE.MeshPhysicalMaterial {
  return applyOneQodeShader(new THREE.MeshPhysicalMaterial({
    color: colorHex,
    roughness: 0.48,
    metalness: 0,
    envMapIntensity: 0.12,
    emissive: DEFAULT_EMISSIVE_HEX,
    emissiveIntensity: 0.32,
    clearcoat: 0.15,
    clearcoatRoughness: 0.70,
  }), "accent");
}

export function createBrandWhiteMaterial(): THREE.MeshPhysicalMaterial {
  return applyOneQodeShader(new THREE.MeshPhysicalMaterial({
    color: DEFAULT_BRAND_WHITE_HEX,
    roughness: 0.58,
    metalness: 0,
    envMapIntensity: 0.18,
    emissive: 0xd7e4f2,
    emissiveIntensity: 0.06,
    clearcoat: 0.18,
    clearcoatRoughness: 0.62,
  }), "satin");
}

export function applyWholeObjectMaterial(
  group: THREE.Object3D,
  accented: boolean,
  colorHex: number = DEFAULT_ACCENT_HEX,
): void {
  const material = accented ? createAccentMaterial(colorHex) : createSurfaceMaterial();
  group.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.material = material;
  });
}

export function applyBrandObjectMaterial(
  group: THREE.Object3D,
  mode: BrandMaterialMode,
  colorHex: number = DEFAULT_ACCENT_HEX,
): void {
  const material = createBrandMaterial(mode, colorHex);
  group.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.material = material;
  });
}

export function createBrandMaterial(
  mode: BrandMaterialMode,
  colorHex: number = DEFAULT_ACCENT_HEX,
): THREE.MeshPhysicalMaterial {
  if (mode === "accent") return createAccentMaterial(colorHex);
  if (mode === "neutral") return createSurfaceMaterial();
  return createBrandWhiteMaterial();
}

/** Marker key on a mesh's userData when its material has been swapped
 *  with the accent override. Lets us cleanly revert in
 *  clearAccentOverride() without re-cloning the master. */
const ACCENT_ORIGINAL_KEY = "accentOriginalMaterial";

export function applyAccentOverride(
  group: THREE.Object3D,
  colorHex: number = DEFAULT_ACCENT_HEX,
): void {
  const accentColor = new THREE.Color(colorHex);
  // Paint every mesh in the object — server-rack and rack-ai (and other
  // multi-mesh masters) used to leak the original surface colour through
  // their top/cap meshes because the loop bailed after the first mesh.
  // The per-mesh userData stash keeps clearAccentOverride() symmetric.
  group.traverse((c) => {
    const m = c as THREE.Mesh;
    if (!m.isMesh) return;
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    if (!m.userData[ACCENT_ORIGINAL_KEY]) {
      m.userData[ACCENT_ORIGINAL_KEY] = m.material;
    }
    const next = mats.map((mat) => {
      const std = mat as THREE.MeshStandardMaterial;
      if (!std.color) return mat;
      const cloned = std.clone();
      cloned.color.copy(accentColor);
      cloned.metalness = 0;
      cloned.roughness = 0.4;
      cloned.envMapIntensity = 0.12;
      cloned.emissive.setHex(DEFAULT_EMISSIVE_HEX);
      cloned.emissiveIntensity = 0.32;
      return applyOneQodeShader(cloned, "accent");
    });
    m.material = (next.length === 1 ? next[0] : next) as THREE.Material | THREE.Material[];
  });
}

/**
 * Revert any accent override applied by `applyAccentOverride()`. Restores
 * the original mesh material(s) we stashed on first apply. Idempotent —
 * a no-op when no override is currently in effect.
 */
export function clearAccentOverride(group: THREE.Object3D): void {
  group.traverse((c) => {
    const m = c as THREE.Mesh;
    if (!m.isMesh) return;
    const original = m.userData[ACCENT_ORIGINAL_KEY] as THREE.Material | THREE.Material[] | undefined;
    if (!original) return;
    // Dispose the cloned accent material(s) we added so we don't leak.
    const current = m.material;
    const arr = Array.isArray(current) ? current : [current];
    for (const mat of arr) (mat as THREE.Material).dispose?.();
    m.material = original;
    delete m.userData[ACCENT_ORIGINAL_KEY];
  });
}
